/**
 * WebSocket message handlers for the Interview component
 * This module breaks down the large onmessage handler into manageable pieces
 */

/**
 * AudioStreamPlayer - Handles streaming audio chunks with buffering
 * Based on FRONTEND_STREAMING_TTS_GUIDE.md specifications
 */
class AudioStreamPlayer {
  constructor() {
    this.audioContext = null;
    this.chunks = [];
    this.isPlaying = false;
    this.currentSource = null;
    this.currentAudioElement = null; // Track current HTML Audio element
  }

  async initialize() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
    }
    
    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  async addChunk(base64Audio, format = 'audio/ogg') {
    try {
      await this.initialize();
      
      // Decode base64
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      this.chunks.push({ bytes, format });
      console.log(`📥 Audio chunk buffered (${bytes.length} bytes, ${this.chunks.length} chunks in buffer)`);
      
      // Start playing if not already playing
      if (!this.isPlaying) {
        this.playNextChunk();
      }
    } catch (error) {
      console.error('❌ Failed to buffer audio chunk:', error);
    }
  }

  async playNextChunk() {
    if (this.chunks.length === 0) {
      console.log('📋 No more chunks in buffer');
      // Don't set isPlaying to false yet - current audio might still be playing
      // Wait a bit to see if more chunks arrive or current audio finishes
      setTimeout(() => {
        if (this.chunks.length === 0 && (!this.currentAudioElement || this.currentAudioElement.paused || this.currentAudioElement.ended)) {
          this.isPlaying = false;
          console.log('✅ All audio chunks played and finished');
        }
      }, 100);
      return;
    }
    
    this.isPlaying = true;
    const chunk = this.chunks.shift();
    
    try {
      // For OGG Opus format, we can use Audio element for better compatibility
      const blob = new Blob([chunk.bytes], { type: chunk.format });
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      this.currentAudioElement = audio;
      
      audio.onended = () => {
        console.log('🔚 Audio chunk ended');
        URL.revokeObjectURL(audioUrl);
        this.currentAudioElement = null;
        this.playNextChunk(); // Play next chunk when current finishes
      };
      
      audio.onerror = (error) => {
        console.error('❌ Failed to play audio chunk:', error);
        URL.revokeObjectURL(audioUrl);
        this.currentAudioElement = null;
        this.playNextChunk(); // Try next chunk
      };
      
      await audio.play();
      console.log('▶️ Playing audio chunk');
    } catch (error) {
      console.error('❌ Failed to play audio chunk:', error);
      this.currentAudioElement = null;
      this.playNextChunk(); // Try next chunk
    }
  }

  reset() {
    this.chunks = [];
    this.isPlaying = false;
    
    // Stop current audio element
    if (this.currentAudioElement) {
      try {
        this.currentAudioElement.pause();
        this.currentAudioElement.currentTime = 0;
      } catch (e) {
        // Ignore if already stopped
      }
      this.currentAudioElement = null;
    }
    
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch (e) {
        // Ignore if already stopped
      }
      this.currentSource = null;
    }
    console.log('🔄 Audio player reset');
  }

  async finalize(callback) {
    console.log('🎬 Finalizing audio playback...');
    // Wait for all chunks to finish playing
    const checkComplete = () => {
      const stillHasChunks = this.chunks.length > 0;
      const stillPlaying = this.isPlaying;
      const audioElementPlaying = this.currentAudioElement && !this.currentAudioElement.paused && !this.currentAudioElement.ended;
      
      if (!stillPlaying && !stillHasChunks && !audioElementPlaying) {
        console.log('✅ Audio streaming finalized - all chunks played and audio ended');
        if (callback) {
          // Add small delay to ensure last audio has fully ended
          setTimeout(() => {
            callback();
          }, 250);
        }
      } else {
        console.log(`⏳ Waiting for audio to complete: ${this.chunks.length} chunks remaining, isPlaying: ${this.isPlaying}, audioElement playing: ${audioElementPlaying}`);
        setTimeout(checkComplete, 150);
      }
    };
    checkComplete();
  }
}

// Global audio player instance for streaming
let globalAudioPlayer = null;

function getAudioPlayer() {
  if (!globalAudioPlayer) {
    globalAudioPlayer = new AudioStreamPlayer();
  }
  return globalAudioPlayer;
}

// Helper function to play audio from base64
const playAudioFromBase64 = (
  base64Audio,
  format = "audio/wav",
  text = "",
  onEndedCallback = null,
  setters = {}
) => {
  const {
    setShowLoader,
    setIsSpeaking,
    setBotSpeechCaption,
    setConversationTranscript
  } = setters;

  try {
    console.log('🎵 Playing audio from base64');
    
    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: format });
    const audioUrl = URL.createObjectURL(blob);
    const audio = new Audio(audioUrl);
    
    audio.volume = 0.8;

    audio.onloadstart = () => {
      setShowLoader?.(false);
      setIsSpeaking?.(true);
      
      if (text) {
        setBotSpeechCaption?.(text);
        setConversationTranscript?.(prev => [
          ...prev,
          {
            type: "bot",
            text: text,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      }
    };

    audio.onended = () => {
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
      
      if (onEndedCallback && typeof onEndedCallback === "function") {
        try {
          onEndedCallback();
        } catch (error) {
          console.error("🎵 Error executing audio callback:", error);
        }
      }
    };

    audio.onerror = (error) => {
      // Silently handle audio errors during normal operation
      // These often occur during normal audio transitions and don't indicate real problems
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
    };

    audio.play().catch(error => {
      console.error('❌ Failed to play audio:', error);
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
    });

  } catch (error) {
    console.error('❌ Error processing base64 audio:', error);
    setIsSpeaking?.(false);
  }
};

/**
 * Handle bot_speaking_started message (Streaming TTS)
 * ⚡ CRITICAL: Close microphone IMMEDIATELY when bot starts speaking
 */
export const handleBotSpeakingStarted = (response, context) => {
  const {
    audioRecording,
    setIsAITalking,
    setLiveCaption,
    setStreamingTranscript,
    setShowLoader,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted
  } = context;

  // Don't process if interview is terminated
  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    console.log('🚫 Ignoring bot_speaking_started - interview terminated');
    return;
  }

  console.log('🤖 Bot speaking started:', response.text);
  console.log('⏱️ Bot speaking started at:', Date.now());

  // ✅ HIDE LOADER IMMEDIATELY - Bot is responding now
  if (setShowLoader) {
    setShowLoader(false);
    console.log('🎬 Loader hidden - bot started speaking');
  }

  // ✅ CLOSE MIC IMMEDIATELY - This is the key optimization!
  // Use suspendRecording to pause without tearing down the audio pipeline
  if (audioRecording) {
    console.log('🔍 Mic state check:', {
      isRecording: audioRecording.isRecording,
      isStarting: audioRecording.isStarting,
      recordingState: audioRecording.recordingState
    });
    
    // Suspend recording if active (keeps pipeline alive for quick resume)
    if (audioRecording.isRecording || audioRecording.isStarting) {
      console.log('🔇 Suspending mic instantly (bot_speaking_started)');
      if (audioRecording.suspendRecording) {
        audioRecording.suspendRecording();
      } else {
        // Fallback to stopRecording if suspendRecording is not available
        console.warn('⚠️ suspendRecording not available, using stopRecording');
        audioRecording.stopRecording();
      }
    } else {
      console.log('ℹ️ Mic already inactive - state:', audioRecording.recordingState);
    }
  } else {
    console.warn('⚠️ audioRecording object not available');
  }

  // Clear any live captions/transcripts
  if (setLiveCaption) setLiveCaption('');
  if (setStreamingTranscript) setStreamingTranscript('');

  // Set AI talking state
  if (setIsAITalking) setIsAITalking(true);

  // Reset audio player for new streaming session
  const audioPlayer = getAudioPlayer();
  audioPlayer.reset();
};

/**
 * Handle audio_chunk message (Streaming TTS)
 * Buffers and plays audio chunks as they arrive
 */
export const handleAudioChunk = async (response, context) => {
  const {
    audioRecording,
    setIsAITalking,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted
  } = context;

  // Don't process if interview is terminated
  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    console.log('🚫 Ignoring audio_chunk - interview terminated');
    return;
  }

  const audioPlayer = getAudioPlayer();

  // Check if this is the final chunk
  if (response.is_final) {
    console.log('✅ Final audio chunk received - streaming complete');
    
    // Finalize playback and reopen mic after completion
    await audioPlayer.finalize(() => {
      console.log('🎤 Reopening mic after streaming complete');
      
      // Reopen microphone with a slight delay
      setTimeout(async () => {
        let animationStopped = false; // Flag to prevent multiple calls to setIsAITalking(false)
        
        if (audioRecording) {
          console.log('🔍 Attempting to reopen mic - state:', {
            isRecording: audioRecording.isRecording,
            recordingState: audioRecording.recordingState
          });
          
          if (!audioRecording.isRecording) {
            try {
              // Resume the suspended recording (much faster than starting fresh)
              if (audioRecording.resumeRecording) {
                console.log('🎤 Resuming suspended recording');
                audioRecording.resumeRecording();
                console.log('✅ Mic resumed successfully');
              } else {
                // Fallback to starting fresh if resume is not available
                console.log('🎤 Starting fresh recording (fallback)');
                const success = await audioRecording.startRecording();
                console.log('🎤 Mic reopened:', success ? 'successful' : 'failed');
              }
              
              // ✅ Stop AI talking animation ONLY once after mic is reopened
              if (setIsAITalking && !animationStopped) {
                animationStopped = true;
                setIsAITalking(false);
                console.log('🎬 AI wave animation stopped - mic is now open for user');
              }
            } catch (error) {
              console.error('❌ Failed to reopen mic:', error);
              // Stop animation even if mic fails to reopen
              if (setIsAITalking && !animationStopped) {
                animationStopped = true;
                setIsAITalking(false);
              }
            }
          } else {
            console.log('ℹ️ Mic already recording, skipping reopen');
            // Stop animation since mic is already open
            if (setIsAITalking && !animationStopped) {
              animationStopped = true;
              setIsAITalking(false);
            }
          }
        } else {
          console.warn('⚠️ audioRecording object not available');
          // Stop animation as fallback
          if (setIsAITalking && !animationStopped) {
            animationStopped = true;
            setIsAITalking(false);
          }
        }
      }, 500);
    });
    return;
  }

  // Process audio chunk if present
  if (response.audio && response.audio.length > 0) {
    const format = response.format || 'audio/ogg';
    const codec = response.codec || 'opus';
    const fullFormat = codec ? `${format};codecs=${codec}` : format;
    
    console.log(`📥 Audio chunk received (format: ${fullFormat}, size: ${response.audio.length})`);
    
    // Add chunk to player buffer
    await audioPlayer.addChunk(response.audio, fullFormat);
  }
};

/**
 * Handle synthesized speech messages (Fallback for complete audio)
 */
export const handleSynthesizedSpeech = (response, context) => {
  const {
    setMessages,
    playAudio,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted,
    audioRecording,
    setIsAITalking,
    hasReceivedInitialResponse,
    setHasReceivedInitialResponse,
    sessionId,
    setLiveCaption,
    setShowLoader
  } = context;

  // Don't play synthesized audio if termination/completion is in progress
  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    return;
  }

  // Check for session information to prevent duplicate responses
  const responseSessionId = response.session_id;
  const isReconnection = response.is_reconnection_response;
  
  // If this is a reconnection response and we've already received initial content, skip it
  if (isReconnection && hasReceivedInitialResponse) {
    console.log('🔄 Skipping duplicate AI response after reconnection');
    return;
  }
  
  // If this is the first response, mark it as received
  if (!hasReceivedInitialResponse) {
    setHasReceivedInitialResponse(true);
    console.log('✅ First AI response received and marked');
  }

  console.log('🎵 Received synthesized audio (fallback mode), suspending recording and playing');
  
  // ✅ HIDE LOADER IMMEDIATELY - Bot is responding now
  if (setShowLoader) {
    setShowLoader(false);
    console.log('🎬 Loader hidden - bot started speaking (fallback mode)');
  }
  
  // Clear live caption immediately when AI starts responding
  if (setLiveCaption) {
    setLiveCaption('');
    console.log('🧹 Cleared live caption for AI response');
  }
  
  // Suspend audio recording during AI speech
  if (audioRecording?.isRecording) {
    console.log('🔇 Suspending user audio recording during AI response');
    audioRecording.suspendRecording();
  }
  
  // Set AI talking state
  setIsAITalking?.(true);
  
  const format = response.format || "audio/wav";
  const speechText = response.text || "AI is responding...";
  
  // Enhanced playAudio with callback to resume recording
  const onAudioEnded = () => {
    console.log('🎤 AI finished speaking, starting fresh user audio recording');
    
    let animationStopped = false; // Flag to prevent multiple calls to setIsAITalking(false)
    
    // Always start fresh recording after AI speech - with reasonable delay to prevent rapid cycling
    if (audioRecording) {
      setTimeout(async () => {
        try {
          console.log('🎤 Starting fresh recording after AI speech');
          const success = await audioRecording.startRecording();
          console.log('✅ Audio recording start attempt:', success ? 'successful' : 'failed');
          
          // ✅ Stop AI talking animation ONLY once after mic operation completes
          if (!animationStopped) {
            animationStopped = true;
            setIsAITalking?.(false);
            console.log('🎬 AI wave animation stopped - mic is now open for user (fallback mode)');
          }
        } catch (error) {
          console.error('❌ Failed to start audio recording:', error);
          // Stop animation even on error, but only once
          if (!animationStopped) {
            animationStopped = true;
            setIsAITalking?.(false);
          }
        }
      }, 800); // Increased delay to prevent rapid start/stop cycles and ensure system stability
    } else {
      console.warn('⚠️ audioRecording object not available');
      // Stop animation as fallback, but only once
      if (!animationStopped) {
        animationStopped = true;
        setIsAITalking?.(false);
      }
    }
  };
  
  playAudio(response.audio, format, speechText, onAudioEnded);

  // Add to chat if there's accompanying text
  if (response.text) {
    setMessages(prev => [
      ...prev,
      {
        type: "ai",
        text: response.text,
        session_id: responseSessionId,
        timestamp: Date.now()
      },
    ]);
  }
};

/**
 * Handle streaming interim transcript messages (new enhanced format)
 */
export const handleStreamingInterimTranscript = (response, context) => {
  const {
    setStreamingTranscript,
    setLiveCaption,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted
  } = context;

  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    console.log('🚫 Ignoring streaming transcript - interview terminated');
    return;
  }

  const transcript = response.transcript;
  
  if (!transcript || (typeof transcript === 'string' ? !transcript.trim() : !transcript.text)) {
    return;
  }

  // Handle both string and object transcript formats
  const transcriptText = typeof transcript === 'string' ? transcript : transcript.text;
  const confidence = typeof transcript === 'object' ? transcript.confidence : response.confidence;

  console.log('📝 Received streaming interim transcript:', transcriptText);

  // Update streaming transcript state - this should show in the UI
  if (setStreamingTranscript) {
    setStreamingTranscript(transcriptText);
  }
  
  // Also update live caption for backward compatibility
  if (setLiveCaption) {
    setLiveCaption(transcriptText);
  }
};

/**
 * Handle final consolidated transcript messages (new enhanced format)
 * 🎤 CRITICAL: Microphone closure happens FIRST, regardless of transcript content
 */
export const handleFinalConsolidatedTranscript = (response, context) => {
  const {
    setStreamingTranscript,
    setLiveCaption,
    setMessages,
    setConversationTranscript,
    setShowLoader,
    setLoaderText,
    isTerminationInProgress,
    isInterviewCompleted,
    isInterviewTerminated,
    audioRecording // Access to microphone control
  } = context;

  // 🎤 BACKEND-DRIVEN MIC CLOSURE: Backend detected speech end via Google STT VAD
  // ⚡ CLOSE MIC IMMEDIATELY - This happens FIRST, before any validation
  // The message type itself (final_consolidated_transcript) is the authoritative signal
  if (audioRecording?.isRecording) {
    console.log('🎤 Backend signaled speech end - Auto-closing microphone');
    audioRecording.stopRecording();
  }

  // Clear streaming states immediately (regardless of transcript content)
  setStreamingTranscript("");
  setLiveCaption("");

  // Validate transcript content - only affects whether we store/display it
  if (!response.transcript || !response.transcript.text) {
    console.warn('⚠️ Received final_consolidated_transcript with empty text - mic closed but no transcript to store');
    
    // Still show loader for potential AI response even without transcript
    if (!isTerminationInProgress && !isInterviewCompleted && !isInterviewTerminated) {
      setShowLoader(true);
      setLoaderText("");
    }
    return;
  }

  const transcript = response.transcript;
  const consolidationInfo = response.consolidation_info;
  const qualityMetrics = response.quality_metrics;

  console.log('✅ Received final consolidated transcript:', {
    text: transcript.text,
    confidence: transcript.confidence,
    wordCount: qualityMetrics?.word_count,
    consolidationMethod: consolidationInfo?.method,
    segments: consolidationInfo?.total_segments
  });
  
  // Add to chat and conversation transcript
  setMessages(prev => [
    ...prev,
    { 
      type: "user", 
      text: transcript.text,
      metadata: {
        confidence: transcript.confidence,
        wordCount: qualityMetrics?.word_count,
        consolidationInfo: consolidationInfo
      }
    }
  ]);

  setConversationTranscript(prev => [
    ...prev,
    {
      type: "user",
      text: transcript.text,
      timestamp: new Date().toLocaleTimeString(),
      confidence: transcript.confidence,
      qualityMetrics: qualityMetrics
    }
  ]);

  // Show processing loader for AI response
  if (!isTerminationInProgress && !isInterviewCompleted && !isInterviewTerminated) {
    setShowLoader(true);
    setLoaderText(""); // Using loader dots animation instead of text
  }
};

/**
 * Handle enhanced STT error messages
 */
export const handleEnhancedSTTError = (response, context) => {
  console.error('❌ Enhanced STT Error:', response.error);
  
  // For now, just log the error. In the future, we can add user-friendly error handling
  const error = new Error(`STT Error: ${response.error}`);
  
  // Optional error handlers if they exist in context
  if (context.setLastError) context.setLastError(error);
  if (context.onSTTError) context.onSTTError(error, response);
};
export const handleLiveCaption = (response, context) => {
  const {
    backendSTTEnabled,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted,
    setLiveCaption
  } = context;

  if (!backendSTTEnabled || isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    return;
  }

  const newText = response.text || "";
  
  // Update live caption (simplified version)
  if (setLiveCaption && newText.trim()) {
    setLiveCaption(newText);
  }
  
  console.log('📝 Received live caption from backend:', response.text);
};

/**
 * Handle final transcript messages from backend STT
 */
export const handleTranscriptFinal = (response, context) => {
  const {
    backendSTTEnabled,
    setLiveCaption,
    setStreamingTranscript,
    setMessages,
    setConversationTranscript,
    setShowLoader,
    setLoaderText,
    isTerminationInProgress,
    isInterviewCompleted,
    isInterviewTerminated
  } = context;

  if (!backendSTTEnabled || !response.text) {
    return;
  }

  // Clear live captions
  if (setLiveCaption) setLiveCaption("");
  if (setStreamingTranscript) setStreamingTranscript("");
  
  // Add to chat and conversation transcript
  setMessages(prev => [
    ...prev,
    { type: "user", text: response.text }
  ]);

  setConversationTranscript(prev => [
    ...prev,
    {
      type: "user",
      text: response.text,
      timestamp: new Date().toLocaleTimeString(),
    }
  ]);

  // Show processing loader
  if (!isTerminationInProgress && !isInterviewCompleted && !isInterviewTerminated) {
    setShowLoader(true);
    setLoaderText("Processing your response...");
    setTimeout(() => setShowLoader(false), 1000);
  }

  console.log('✅ Received final transcript from backend:', response.text);
};

/**
 * Handle ping messages (health check)
 */
export const handlePing = (response, context) => {
  const { sendMessage } = context;
  
  const pongMessage = {
    type: "pong",
    timestamp: new Date().toISOString()
  };
  
  if (sendMessage) {
    sendMessage(pongMessage);
    console.log('🏓 Responded to ping with pong');
  }
};

/**
 * Handle video URL acknowledgment
 */
export const handleVideoUrlReceived = (response, context) => {
  const {
    setVideoUrlSent,
    setPendingVideoUrl,
    setVideoUrlRetryCount,
    setLoaderText,
    cleanupStoredVideoUrls,
    getInterviewSlotId
  } = context;

  console.log('✅ Backend confirmed video URL received:', response);
  
  setVideoUrlSent(true);
  setPendingVideoUrl(null);
  setVideoUrlRetryCount(0);

  // Update loader text to show completion
  setLoaderText("Video upload confirmed by backend!");

  // Stop all ongoing processes immediately
  if (window.stopS3UploadCheck) {
    console.log('🎥 Stopping S3 upload check due to video URL acknowledgment');
    window.stopS3UploadCheck();
    delete window.stopS3UploadCheck;
  }
  window.conclusionAcknowledgmentInProgress = false;

  // Clear any conclusion audio timeouts
  if (window.conclusionTimeoutId) {
    clearTimeout(window.conclusionTimeoutId);
    window.conclusionTimeoutId = null;
  }

  // Clean up any stored fallback data
  const interviewId = response.interview_id || getInterviewSlotId();
  if (interviewId) {
    cleanupStoredVideoUrls(interviewId);
  }

  // Store acknowledgment in localStorage to prevent re-sending across sessions
  try {
    localStorage.setItem(`video_url_acknowledged_${interviewId}`, "true");
  } catch (error) {
    console.warn("Could not store video URL acknowledgment:", error);
  }

  console.log('🎥 Video URL acknowledgment processed, stopping retries');
};

/**
 * Handle conclusion message (final speech before termination)
 */
export const handleConclusionMessage = (response, context) => {
  const {
    setIsConclusionAudioPlaying,
    stopAllRecording,
    stopScreenRecording,
    setMessages,
    playAudio,
    createConclusionAcknowledgment,
    setIsAITalking,
    completeInterview
  } = context;

  console.log('🎵 Received conclusion message');
  
  // Set a flag to track that we received conclusion message
  window.conclusionMessageReceived = true;

  // Set conclusion audio playing state to prevent video URL retries
  setIsConclusionAudioPlaying(true);
  
  // ✅ Start sine wave animation for conclusion audio
  if (setIsAITalking) {
    setIsAITalking(true);
    console.log('🎬 Starting AI wave animation for conclusion audio');
  }

  // Stop all recording activities
  stopAllRecording();
  stopScreenRecording();

  // Store conclusion text in chat
  if (response.text) {
    setMessages(prev => [
      ...prev,
      {
        type: "ai",
        text: response.text,
      },
    ]);
  }

  // Create and execute conclusion acknowledgment
  const sendConclusionAcknowledment = createConclusionAcknowledgment();
  
  // Enhanced callback that stops animation after acknowledgment
  const conclusionCallback = async () => {
    try {
      console.log('🎵 Conclusion audio ended, sending acknowledgment');
      console.log('🎵 DEBUG: conclusionCallback called successfully');
      
      // Mark that callback fired successfully
      window.interviewCompletedViaCallback = true;
      
      // Clear the short fallback timeout since callback fired
      if (window.conclusionCompletionFallbackId) {
        clearTimeout(window.conclusionCompletionFallbackId);
        window.conclusionCompletionFallbackId = null;
      }
      
      // ✅ Stop sine wave animation when conclusion audio ends
      if (setIsAITalking) {
        setIsAITalking(false);
        console.log('🎬 Stopping AI wave animation - conclusion audio complete');
      }
      
      console.log('🎵 DEBUG: About to call sendConclusionAcknowledment');
      await sendConclusionAcknowledment();
      console.log('🎵 DEBUG: sendConclusionAcknowledment completed successfully');
    } catch (error) {
      console.error('❌ ERROR in conclusionCallback:', error);
      // Still try to send acknowledgment even if there's an error
      try {
        await sendConclusionAcknowledment();
      } catch (fallbackError) {
        console.error('❌ FALLBACK ERROR in conclusionCallback:', fallbackError);
      }
    }
  };

  // Play conclusion audio with enhanced callback
  if (response.audio) {
    const format = response.format || "audio/wav";
    playAudio(response.audio, format, response.text, conclusionCallback);

    // Shorter fallback timeout (15 seconds) to ensure completion even if audio fails
    window.conclusionCompletionFallbackId = setTimeout(() => {
      console.log('🎵 Short fallback: Ensuring interview completion after 15 seconds');
      if (!window.interviewCompletedViaCallback && completeInterview) {
        console.log('🎵 Audio callback did not fire in time, forcing completion');
        completeInterview();
        window.interviewCompletedViaCallback = true;
      }
      if (setIsAITalking) setIsAITalking(false);
      if (setIsConclusionAudioPlaying) setIsConclusionAudioPlaying(false);
    }, 15000); // 15 second fallback

    // Longer fallback timeout for full acknowledgment flow
    window.conclusionTimeoutId = setTimeout(async () => {
      console.log('🎵 Fallback: Executing conclusion acknowledgment after timeout');
      
      // Stop animation in fallback case too
      if (setIsAITalking) {
        setIsAITalking(false);
        console.log('🎬 Stopping AI wave animation - fallback timeout');
      }
      
      await sendConclusionAcknowledment();
      window.conclusionTimeoutId = null;
    }, 90000); // 90 second fallback for full flow
  } else {
    // If no audio, mark completion immediately and stop animation
    console.log('🎵 No audio in conclusion, completing interview immediately');
    if (completeInterview) {
      completeInterview();
      window.interviewCompletedViaCallback = true;
    }
    setIsConclusionAudioPlaying(false);
    if (setIsAITalking) setIsAITalking(false);
    sendConclusionAcknowledment();
  }
};

/**
 * Handle interview completion confirmation
 */
export const handleInterviewCompleted = (response, context) => {
  const {
    setShowLoader,
    setLoaderText,
    clearAllRetries,
    stopAllAudio,
    disconnectWebSocket,
    cleanupStoredVideoUrls,
    getInterviewSlotId,
    completeInterview,
    setIsConclusionAudioPlaying
  } = context;

  console.log('🏁 Interview completion confirmed by backend:', response);
  
  // ✅ CRITICAL: Mark interview as completed in the UI state
  // This triggers the completed dialog to show
  if (completeInterview) {
    console.log('🏁 Calling completeInterview() to show completed dialog');
    completeInterview();
  }
  
  // Clear conclusion audio playing state
  if (setIsConclusionAudioPlaying) {
    setIsConclusionAudioPlaying(false);
  }

  // Stop any ongoing processes
  if (window.stopS3UploadCheck) {
    window.stopS3UploadCheck();
    delete window.stopS3UploadCheck;
  }
  window.conclusionAcknowledgmentInProgress = false;

  // Clear all retries and timeouts
  if (clearAllRetries) clearAllRetries();

  if (window.conclusionTimeoutId) {
    clearTimeout(window.conclusionTimeoutId);
    window.conclusionTimeoutId = null;
  }

  // Update UI
  if (setLoaderText) setLoaderText("Interview successfully completed!");
  if (setShowLoader) setTimeout(() => setShowLoader(false), 2000);

  // Clean up resources
  if (stopAllAudio) stopAllAudio();
  
  // Clean up stored video URLs
  if (cleanupStoredVideoUrls && getInterviewSlotId) {
    const interviewId = response.interview_slot_id || getInterviewSlotId();
    cleanupStoredVideoUrls(interviewId);
  }

  // Disconnect WebSocket after a delay
  if (disconnectWebSocket) {
    setTimeout(() => {
      disconnectWebSocket();
    }, 1000);
  }
};

/**
 * Handle evaluation response from bot
 */
export const handleEvaluationComplete = (response, context) => {
  const { storeEvaluationResult } = context;

  console.log('📊 Received evaluation from bot:', response);
  
  const evaluationData = response.evaluation || response;
  
  // Store the evaluation
  if (storeEvaluationResult) {
    storeEvaluationResult(evaluationData).catch(error => {
      console.error('❌ Failed to store evaluation:', error);
    });
  }
};

/**
 * Main message router that delegates to specific handlers
 */
export const handleWebSocketMessage = (data, context) => {
  try {
    const response = typeof data === 'string' ? JSON.parse(data) : data;
    
    console.log(`📨 Processing WebSocket message: ${response.type}`);

    switch (response.type) {
      // ============ STREAMING TTS MESSAGES ============
      case 'bot_speaking_started':
        handleBotSpeakingStarted(response, context);
        break;
        
      case 'audio_chunk':
        handleAudioChunk(response, context);
        break;
        
      case 'synthesized_speech':
        // Fallback for complete audio (non-streaming)
        console.log('📦 Received complete audio (fallback mode)');
        handleSynthesizedSpeech(response, context);
        break;
        
      // ============ ENHANCED STT MESSAGES ============
      case 'streaming_interim_transcript':
      case 'interim_transcript':
        handleStreamingInterimTranscript(response, context);
        break;
        
      case 'final_consolidated_transcript':
        handleFinalConsolidatedTranscript(response, context);
        break;
        
      case 'enhanced_stt_error':
        handleEnhancedSTTError(response, context);
        break;
        
      // Legacy STT message types (for backward compatibility)
      case 'live_caption':
        handleLiveCaption(response, context);
        break;
        
      case 'transcript_final':
        handleTranscriptFinal(response, context);
        break;
        
      case 'ping':
        handlePing(response, context);
        break;
        
      case 'transcript_received':
        // Handle backend acknowledgment of received transcript
        console.log('✅ Backend acknowledged transcript receipt:', response);
        break;
        
      case 'video_url_received':
        handleVideoUrlReceived(response, context);
        break;
        
      case 'conclusion_message':
        handleConclusionMessage(response, context);
        break;
        
      case 'interview_completed':
        handleInterviewCompleted(response, context);
        break;
        
      case 'evaluation_complete':
      case 'evaluation':
        handleEvaluationComplete(response, context);
        break;
        
      case 'bot_response':
        // Handle bot_response - this might contain text or other data
        console.log('🤖 Received bot response:', response);
        if (response.text) {
          const { setMessages } = context;
          setMessages(prev => [
            ...prev,
            {
              type: "ai",
              text: response.text,
              timestamp: Date.now()
            },
          ]);
        }
        break;
        
      case 'connection_established':
        console.log('🔗 WebSocket connection established');
        break;
        
      case 'interview_started':
        console.log('🎬 Backend confirmed interview started');
        break;
        
      case 'error':
        console.warn('⚠️ Backend error:', response.message || response.error || 'Unknown error');
        break;
        
      default:
        console.warn(`❓ Unknown message type: ${response.type}`);
        // Handle unknown message types gracefully
        if (response.evaluation) {
          handleEvaluationComplete(response, context);
        }
        break;
    }
  } catch (error) {
    console.error('❌ Error processing WebSocket message:', error);
    console.log('Raw message:', data);
    
    // Don't throw the error, just log it to prevent UI crashes
    return;
  }
};

export default {
  handleWebSocketMessage,
  handleBotSpeakingStarted,
  handleAudioChunk,
  handleSynthesizedSpeech,
  handleStreamingInterimTranscript,
  handleFinalConsolidatedTranscript,
  handleEnhancedSTTError,
  handleLiveCaption,
  handleTranscriptFinal,
  handlePing,
  handleVideoUrlReceived,
  handleConclusionMessage,
  handleInterviewCompleted,
  handleEvaluationComplete
};
